import { Button, TableCell, TableRow } from "@mui/material";
import SharedTableContent from "./SharedTableContent";
import { formatDollarsCurrency, getAction, numberFormatter } from "../OptionsTrading/helpers";
import { useEffect, useState } from "react";
import usePolling from "../../hooks/usePolling";
import ModalParent from "../OptionsTrading/ModalParent";
import { isLimitOrder, isMultiLegOrder } from "./OrdersTable";
import { useSelector } from "react-redux";
import { StatusChip } from "./StatusChip";
import { TradeStrategies } from "../../assets/interfaces/interfaces";

const getOptionSymbolsForFilledOrders = (order) => {
  if (isMultiLegOrder(order)) {
    return order.legs.map(leg => leg.option_symbol);
  }
  if (isLimitOrder(order)) {
    return (
      order.raw_order?.option_symbol || order.raw_order?.["option_symbol[0]"]
    );
  }
  return order.option_symbol;
};

export const calculateProfitLossForSingle = (order, midPriceValue) => {
  if (midPriceValue === null) {
    return null;
  }

  const currentContractPrice
      = midPriceValue * 100 * parseInt(order.total_quantity, 10) || 0;
  const initialContractPrice
      = parseFloat(order.avg_fill_price)
      * 100
      * parseInt(order.exec_quantity || 0);
  const profitOrLoss = currentContractPrice - initialContractPrice;
  return profitOrLoss;
};

// get the parent leg for limit orders
export const getPrimaryOrderForLimitOrders = (legs) => {
  const primaryOrder = legs.find(leg => leg.side.endsWith("to_open"));
  return primaryOrder;
};

const getOrderQuantity = (order) => {
  if (order.total_quantity === null || Number.isNaN(Number(order.total_quantity))) {
    const primaryOrder = getPrimaryOrderForLimitOrders(order.legs);
    return primaryOrder.total_quantity || "N/A";
  }
  return order.total_quantity;
};

const OpenPositions = ({ filledOrders, findTradeByOrderId }) => {
  const [showClosePositionModal, setShowClosePositionModal] = useState(false);
  const [selectedPositionInfo, setSelectedPositionInfo] = useState({
    selectedPosition: null,
    parentId: "",
  });
  const [marketData, setMarketData] = useState(null);
  const virtualAccount = useSelector(
    (state: { tradierAccount }) => state.tradierAccount.account,
  );

  console.log("filled orders", filledOrders);

  const optionSymbolsString = filledOrders
    .flatMap(order => getOptionSymbolsForFilledOrders(order))
    .join(",");

  const { data } = usePolling(
    optionSymbolsString
      ? `https://sandbox.tradier.com/v1/markets/quotes?symbols=${optionSymbolsString}&greeks=false`
      : null,
    60000,
    virtualAccount?.access_token,
  );

  useEffect(() => {
    if (data && data.quotes) {
      const quotes = Array.isArray(data?.quotes.quote)
        ? data.quotes.quote
        : [data?.quotes.quote];

      const quotesObj = Object.fromEntries(quotes.map(quote => [quote.symbol, quote]));
      setMarketData(quotesObj);
    }
  }, [data]);

  const getMidPrice = (optionSymbol) => {
    const data = marketData?.[optionSymbol];
    if (!data) {
      // console.error("marketData is not properly initialized.");
      return null;
    }
    const bid = typeof data.bid === "number" ? data.bid : 0;
    const ask = typeof data.ask === "number" ? data.ask : 0;

    return (bid + ask) / 2;
  };

  const getMidPriceForEachLeg = (leg) => {
    const midPriceForLeg = getMidPrice(leg.option_symbol);
    return leg.side === "sell_to_open" || leg.side === "sell_to_close"
      ? midPriceForLeg
      : -midPriceForLeg;
  };

  const getNetCreditOrDebit = (legs) => {
    return legs.reduce((total, leg) => {
      return total + getMidPriceForEachLeg(leg);
    }, 0);
  };

  const calculateProfitLossForMulti = (order) => {
    const avgFillPrice = parseFloat(order.avg_fill_price);
    const totalQuantity = parseInt(order.total_quantity, 10);
    if (isNaN(avgFillPrice) || isNaN(totalQuantity)) {
      return 0;
    }
    const initialDebitCredit = avgFillPrice * 100 * totalQuantity;
    const netCreditOrDebit = getNetCreditOrDebit(order.legs) * 100;
    return initialDebitCredit - netCreditOrDebit;
  };

  const calculateValueForMultiLeg = (order, trade) => {
    const initialMargin = trade?.preview_result?.orders[0].margin_change || 0;
    const netCreditDebit = getNetCreditOrDebit(order.legs);
    const currentMarketValue
      = -netCreditDebit * parseInt(order.total_quantity) * 100 + initialMargin;
    return currentMarketValue;
  };

  const calculateMidPriceDiff = (order) => {
    const midPriceValue = getMidPrice(order.option_strategy);
    const avgFillPrice = parseFloat(order.avg_fill_price);
    if (midPriceValue === null) {
      return "N/A";
    }
    const avgPriceDifference = (
      ((avgFillPrice - midPriceValue) / avgFillPrice)
      * 100
    ).toFixed(2);
    return parseFloat(avgPriceDifference);
  };

  const getCurrentContractPriceForSingle = (order) => {
    const midPriceValue = getMidPrice(order?.option_symbol);
    return midPriceValue * 100 * parseInt(order?.total_quantity || 0);
  };

  return (
    <>
      {showClosePositionModal && (
        <ModalParent
          open={showClosePositionModal}
          onClose={() => setShowClosePositionModal(false)}
          orderToClose={selectedPositionInfo.selectedPosition}
          positionType="close"
          selectedTrade={findTradeByOrderId(selectedPositionInfo.parentId)}
        />
      )}
      {filledOrders.map((order) => {
        const isMultiLeg = isMultiLegOrder(order);
        const isLimit = isLimitOrder(order);
        const associatedTrade = findTradeByOrderId(order.external_order_id);
        const primaryOrder = isLimit ? getPrimaryOrderForLimitOrders(order.legs) : null;
        const optionSymbol = order.option_symbol || primaryOrder?.option_symbol;

        const midPriceForSingle = getMidPrice(optionSymbol);

        return (
          <TableRow key={order.id} className="body2">
            <SharedTableContent order={order} hasLegs={isMultiLeg} />
            {isMultiLeg
              ? (
                  <TableCell>
                    {order.legs.map((leg, index) => (
                      <TableRow key={`leg-${order.id}-${index}`}>
                        <TableCell className="nested-row">
                          {getAction(leg.side)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableCell>
                )
              : (
                  <TableCell>{getAction(order.side)}</TableCell>
                )}
            {isMultiLeg
              ? (
                  <TableCell id="qty">
                    {order.legs.map((leg, index) => (
                      <TableRow key={`leg-${order.id}-${index}`}>
                        <TableCell className="nested-row">
                          {leg.total_quantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableCell>
                )
              : (
                  <TableCell id="qty">{getOrderQuantity(order)}</TableCell>
                )}

            <TableCell id="midPrice">
              {!isMultiLeg && (
                <>
                  {(() => {
                    const percentageDiff = calculateMidPriceDiff(primaryOrder || order);

                    return (
                      <>
                        <div>
                          {isNaN(midPriceForSingle)
                            ? ""
                            : numberFormatter(midPriceForSingle, 3)}
                        </div>
                        {typeof percentageDiff === "number" && (
                          <p
                            className={
                              percentageDiff > 0
                                ? "text-mui-green-text-success"
                                : "text-mui-primary-red-light"
                            }
                          >
                            {percentageDiff > 0
                              ? `+${percentageDiff}%`
                              : `-${Math.abs(percentageDiff)}%`}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </TableCell>
            {isMultiLeg
              ? (
                  <TableCell id="netCreditDebit">
                    <TableRow key={`leg-${order.id}`}>
                      <TableCell className="nested-row">
                        {(() => {
                          const netCreditOrDebit = getNetCreditOrDebit(order.legs);
                          const isCredit = netCreditOrDebit > 0;
                          const formattedValue = isCredit
                            ? `-${Math.abs(netCreditOrDebit).toFixed(3)} DR`
                            : `+${Math.abs(netCreditOrDebit).toFixed(3)} CR`;

                          return (
                            <p
                              className={
                                isCredit
                                  ? "text-mui-primary-red-light"
                                  : "text-mui-green-text-success"
                              }
                            >
                              {formattedValue}
                            </p>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  </TableCell>
                )
              : (
                  <TableCell id="netCreditDebit"></TableCell>
                )}
            {isMultiLeg
              ? (
                  <TableCell id="profitloss">
                    {(() => {
                      const profitLoss = calculateProfitLossForMulti(order);
                      const isPositive = !isNaN(profitLoss) && profitLoss > 0;
                      const formattedProfitLoss = !isNaN(profitLoss)
                        ? formatDollarsCurrency(Math.abs(profitLoss))
                        : "";

                      return (
                        <p
                          className={
                            isPositive
                              ? "text-mui-green-text-success"
                              : "text-mui-primary-red-light"
                          }
                        >
                          {isPositive
                            ? `+${formattedProfitLoss}`
                            : `-${formattedProfitLoss}`}
                        </p>
                      );
                    })()}
                  </TableCell>
                )
              : (
                  <TableCell id="profitloss">
                    {(() => {
                      const rawProfitLoss = calculateProfitLossForSingle(primaryOrder || order, midPriceForSingle);
                      console.log(rawProfitLoss);
                      const formattedProfitLoss
                        = associatedTrade?.trade_idea?.option_strategy === TradeStrategies.Short_Call
                        || associatedTrade?.trade_idea?.option_strategy === TradeStrategies.Short_Put
                          ? -rawProfitLoss
                          : rawProfitLoss;

                      const profitLossClass = formattedProfitLoss > 0
                        ? "text-mui-green-text-success"
                        : "text-mui-primary-red-light";

                      return (
                        <p className={profitLossClass}>
                          {numberFormatter(formattedProfitLoss)}
                        </p>
                      );
                    })()}
                  </TableCell>
                )}
            {isMultiLeg
              ? (
                  <TableCell id="orderValue">
                    <TableRow key={`leg-${order.id}`}>
                      <TableCell className="nested-row">
                        {(() => {
                          const value = calculateValueForMultiLeg(
                            order,
                            associatedTrade,
                          );
                          return !isNaN(value) ? numberFormatter(value) : "";
                        })()}
                      </TableCell>
                    </TableRow>
                  </TableCell>
                )
              : (
                  <TableCell id="orderValue">
                    {(() => {
                      const cost = associatedTrade?.preview_result?.cost || 0;
                      const total
                    = associatedTrade?.preview_result?.orders[0]?.margin_change
                    > 0
                      ? cost + calculateProfitLossForSingle(primaryOrder || order, midPriceForSingle)
                      : getCurrentContractPriceForSingle(primaryOrder || order);
                      return !isNaN(total) ? numberFormatter(total) : "";
                    })()}
                  </TableCell>
                )}

            <TableCell id="sltp">
              {isLimit
                ? (
                    <StatusChip
                      status={`${numberFormatter(associatedTrade.trade_idea?.stop || 0)} / ${numberFormatter(associatedTrade.trade_idea?.profit || 0)}`}
                    />
                  )
                : (
                    "N/A"
                  )}
            </TableCell>

            <TableCell>
              <Button
                variant="contained"
                color="inherit"
                size="small"
                className="button-submit"
                onClick={() => {
                  if (primaryOrder) {
                    setSelectedPositionInfo({
                      selectedPosition: primaryOrder,
                      parentId: primaryOrder.parent_id,
                    });
                  }
                  else {
                    setSelectedPositionInfo({
                      selectedPosition: order,
                      parentId: order.external_order_id,
                    });
                  }
                  // setSelectedPositionInfo(primaryOrder || order);
                  setShowClosePositionModal(true);
                }}
              >
                Close Position
              </Button>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
};

export default OpenPositions;
